import { GraduationCap, HeartHandshake, Building2, Users, HandCoins } from "lucide-react";
import Reveal from "@/components/Reveal";

const VERTICALS = [
  { icon: Building2, label: "Corporate & CSR Fundraising" },
  { icon: HandCoins, label: "Institutional & Grant Funding" },
  { icon: Users, label: "Individual Donor Programs" },
  { icon: HeartHandshake, label: "Donor Stewardship & Renewals" },
];

export default function Team() {
  return (
    <section id="team" data-testid="team-section" className="bg-[#FBFBF9] py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <Reveal className="max-w-2xl">
          <p className="font-mono-alt mb-4 text-xs font-semibold tracking-[0.35em] text-[#E8862A]">02 / LEADERSHIP</p>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[#C7DAF5] bg-[#EAF1FC] px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-[#0E2A6B]">
            Director & Team
          </span>
          <h2 className="mt-5 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
            Led by a fundraiser,{" "}
            <span className="font-display italic text-[#0E2A6B]">not an agency</span>
          </h2>
        </Reveal>

        <div className="mt-12 grid gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:gap-14">
          <Reveal data-testid="director-card" delay={0.1} className="overflow-hidden rounded-3xl border border-[#E6E9E6] bg-white shadow-xl">
            <div className="relative flex h-72 items-center justify-center bg-gradient-to-br from-[#0E2A6B] via-[#12306E] to-[#0A1A3F]">
              <svg className="absolute inset-0 h-full w-full opacity-50" viewBox="0 0 400 300" fill="none" preserveAspectRatio="xMidYMid slice">
                <circle cx="200" cy="150" r="110" stroke="white" strokeOpacity="0.15" strokeWidth="1.5" strokeDasharray="4 8" />
                <circle cx="200" cy="150" r="70" stroke="white" strokeOpacity="0.2" strokeWidth="1.5" strokeDasharray="4 8" />
                <path d="M40 240 C 130 200, 230 140, 360 40" stroke="#2E6BE6" strokeWidth="6" strokeLinecap="round" />
                <circle cx="80" cy="212" r="11" fill="#E8862A" />
                <circle cx="200" cy="150" r="9" fill="#FFFFFF" />
                <circle cx="345" cy="50" r="10" fill="#2E9E44" />
              </svg>
              <div
                data-testid="director-photo"
                className="relative z-10 h-52 w-52 overflow-hidden rounded-full border-4 border-white/25 shadow-2xl sm:h-56 sm:w-56"
              >
                <img
                  src="/assets/director.png"
                  alt="Akshaya Vats — Founder & Director, Impact Sutra"
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
            <div className="p-6">
              <h3 data-testid="director-name" className="text-xl font-bold text-slate-900">Akshaya Vats</h3>
              <p className="text-sm font-semibold text-[#E8862A]">Founder & Director</p>
              <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#EAF1FC] px-3 py-1 text-xs font-semibold text-[#0E2A6B]">
                <GraduationCap className="h-3.5 w-3.5" />
                IIT BHU Alumnus
              </p>
              <p className="ml-2 mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#FDF1E3] px-3 py-1 text-xs font-semibold text-[#C96F1B]">
                Two Decades of Business & Industry Experience
              </p>
              <p className="mt-4 text-sm leading-relaxed text-slate-600">
                An IIT BHU alumnus with two decades of business and industry experience, Akshaya
                leads Impact Sutra's CSR advisory practice — translating grassroots impact into
                boardroom-ready proposals and standing beside NGOs through every negotiation, from
                first pitch to fund disbursement.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.2} className="flex flex-col justify-center">
            <h3 className="text-xl font-bold text-slate-900 sm:text-2xl">
              A senior team behind every mandate
            </h3>
            <p data-testid="team-description" className="mt-4 text-base leading-relaxed text-slate-600">
              The director is backed by a team with <span className="font-semibold text-slate-900">15+ years
              of fundraising experience across verticals</span> — corporate fundraising, institutional
              and CSR grants, and individual donor programs — built across education, healthcare,
              rural development, and women-empowerment missions.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              That blend matters: corporate CSR demands compliance rigour, while individual giving
              demands storytelling. Our team has run both — so your proposal reads credibly to a CSR
              committee and moves a human being at the same time.
            </p>
            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {VERTICALS.map((v) => (
                <li
                  key={v.label}
                  className="flex items-center gap-3 rounded-2xl border border-[#E6E9E6] bg-white px-4 py-3.5 transition-all hover:-translate-y-0.5 hover:border-[#3D63B8]/40 hover:shadow-md"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#EAF1FC] text-[#0E2A6B]">
                    <v.icon className="h-4 w-4" />
                  </span>
                  <span className="text-sm font-semibold text-slate-800">{v.label}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
